import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { Story } from '@/types/cmsTypes';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { v1 } from '@/infrastructure/api/v1/client';
import { listAll } from '@/infrastructure/api/v1/lists';
import { useAuthUser } from '@/hooks/useAuthUser';
import { invalidateShareLinks } from '@/hooks/useShareLink';
import { shareLinkUrl } from '@/modules/databaseService/scenes/shareLinks';
import { pickPublishedVersion } from '@/modules/storyData/versions';
import type { PlanState } from '@/modules/subscriptionPlan/planState';
import { requireReadyPlan } from '@/modules/subscriptionPlan/usePlanState';
import { ensureProjectMapImage } from '@/utils/ensureProjectMapImage';
import type { PublishingWarningType } from '@/components/dialogs/publishing-warning-dialog';

interface Target {
  sceneInstanceId: string;
  versionNumber: number;
  status: string;
}

export interface LiveActionWarning {
  type: PublishingWarningType;
  liveLinkUrl: string | null;
  onConfirm: () => Promise<void>;
}

type RunWrite = <T>(write: () => Promise<T>) => Promise<T>;

const direct: RunWrite = (write) => write();

export function useStoryLiveActions(story: Story, plan: PlanState, onRefresh?: () => void | Promise<void>, runWrite: RunWrite = direct) {
  const navigate = useNavigate();
  const services = useServices();
  const { user } = useAuthUser();
  const [warning, setWarning] = useState<LiveActionWarning | null>(null);

  const fetchTarget = async (): Promise<Target | null> => {
    const { data, error } = await listAll(page => v1.GET('/api/v1/scenes', {
      params: { query: { storyUniqueId: [story.id], status: ['live', 'pending'], ...page } },
    }));
    const row = error ? null : pickPublishedVersion(data);
    return row ? { sceneInstanceId: row.uniqueId, versionNumber: row.versionNumber, status: row.status } : null;
  };

  const readLiveLinkUrl = async (): Promise<string | null> => {
    try {
      const { live } = await services.scene.getShareLinks(story.id);
      return live ? shareLinkUrl(live.slug) : null;
    } catch (error) {
      console.error('Error reading the live link for the warning:', error);
      return null;
    }
  };

  const afterStatusChange = async () => {
    invalidateShareLinks(story.id);
    if (user?.id) {
      try {
        await ensureProjectMapImage(services, story.projectId, user.id);
      } catch (mapError) {
        console.error('Update project map image after a status change failed', mapError);
      }
    }
    await onRefresh?.();
  };

  const editCopy = async () => {
    if (!user?.id) {
      toast.error('Unable to create copy: missing user information');
      return;
    }
    const userPlan = requireReadyPlan(plan);
    if (!userPlan) return;
    if (!services.subscriptionPlan.hasVersionHistory(userPlan)) {
      toast.error('Your plan does not include version history');
      return;
    }
    const target = await fetchTarget();
    if (!target) {
      toast.error('Could not find published version');
      return;
    }
    try {
      const { sceneInstanceId: newId, versionNumber: newVer } = await services.scene.makeEditableCopy(user.id, target.sceneInstanceId, story.projectId);
      toast.success(`Draft Version ${newVer} created`);
      navigate(`/editor/${newId}`);
    } catch (err) {
      console.error('Error creating editable copy:', err);
      toast.error('Failed to create draft copy');
    }
  };

  const performUnpublish = async (target: Target) => {
    if (!user?.id) return;
    try {
      await runWrite(() => services.scene.unpublishVersion(user.id, story.id, target.versionNumber, story.projectId));
      toast.success(`Version ${target.versionNumber} unpublished`);
    } catch (err) {
      console.error('Error unpublishing:', err);
      toast.error('Failed to unpublish version');
      return;
    }
    await afterStatusChange();
  };

  const unpublish = async () => {
    if (!user?.id) {
      toast.error('Unable to unpublish: missing user information');
      return;
    }
    const target = await fetchTarget();
    if (!target) {
      toast.error('Could not find published version');
      return;
    }
    const willMakeProjectDraft = await services.scene.checkUnpublishWillMakeProjectDraft(story.projectId, target.sceneInstanceId);
    if (target.status !== 'live' && !willMakeProjectDraft) {
      await performUnpublish(target);
      return;
    }
    const liveLinkUrl = target.status === 'live' ? await readLiveLinkUrl() : null;
    setWarning({
      type: willMakeProjectDraft ? 'unpublish-will-make-project-draft' : 'unpublish-will-kill-link',
      liveLinkUrl,
      onConfirm: () => performUnpublish(target),
    });
  };

  const performArchive = async (target: Target) => {
    const result = await runWrite(() => services.scene.handleSceneArchive(target.sceneInstanceId));
    if (!result.success) {
      toast.error(result.message);
      return;
    }
    toast.success(`Version ${target.versionNumber} archived`);
    try {
      await services.project.updateProjectPublishedStatus(story.projectId);
    } catch (updateError) {
      console.error('Error updating project published status after archive:', updateError);
    }
    await afterStatusChange();
  };

  const archive = async () => {
    if (!user?.id) {
      toast.error('Unable to archive: missing user information');
      return;
    }
    const userPlan = requireReadyPlan(plan);
    if (!userPlan) return;
    if (!services.subscriptionPlan.hasVersionHistory(userPlan)) {
      toast.error('Your plan does not include version history');
      return;
    }
    const target = await fetchTarget();
    if (!target) {
      toast.error('Could not find published version');
      return;
    }
    if (target.status !== 'live') {
      await performArchive(target);
      return;
    }
    const [hasOtherLive, liveLinkUrl] = await Promise.all([
      services.scene.checkProjectHasOtherLiveScenes(story.projectId, target.sceneInstanceId),
      readLiveLinkUrl(),
    ]);
    setWarning({
      type: hasOtherLive ? 'archive-will-kill-link' : 'archive-will-unpublish-project',
      liveLinkUrl,
      onConfirm: () => performArchive(target),
    });
  };

  return { warning, clearWarning: () => setWarning(null), editCopy, unpublish, archive };
}
